import './Contacts.css';
import { useLanguage } from 'hooks/useLanguage';
import { useContent } from 'hooks/useContent';
import { SOCIAL_NETWORKS } from 'constants/socialNetworks';

/**
 * Контактна інформація — ADR-0004, ADR-0005, ADR-0017.
 *
 * Прибрано: window.scrollTo(0, 0) прямо в JSX, height: 100vh, скляні плитки з
 * backdrop-filter і жорстко прописані grid-area для кожного блока (через які
 * порядок блоків розсипався при зміні кількості).
 * Виправлено дві помилки в текстах: «Phoone numbers» і «Name of recipient: NGO»
 * без назви організації.
 *
 * Телефони, e-mail, адреса, соцмережі й банківські реквізити — з CMS
 * (api::contact-info, ADR-0017), тому кожен блок рендериться лише тоді, коли
 * відповідне поле прийшло.
 */
export default function Contacts() {
  const language = useLanguage();
  const { data: contactInfo } = useContent('contactInfo');
  const phones = contactInfo?.phones || [];
  const socials = contactInfo?.socials || [];

  return (
    <article className='contacts section'>
      <div className='container'>
        <header className='contacts-head'>
          <h1 className='page-title'>{language === 'en' ? 'Contact information' : 'Контактна інформація'}</h1>
        </header>

        <div className='contacts-info'>
          {phones.length > 0 && (
            <section className='contacts-block'>
              <h2 className='contacts-subtitle'>{language === 'en' ? 'Phone numbers' : 'Номери телефонів'}</h2>
              <ul>
                {phones.map((phone) => (
                  <li className='contact' key={phone.id ?? phone.number}>
                    <a className='tnum' href={`tel:${phone.number.replace(/[^\d+]/g, '')}`}>
                      {phone.number}
                    </a>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {contactInfo?.email && (
            <section className='contacts-block'>
              <h2 className='contacts-subtitle'>{language === 'en' ? 'Email address' : 'Електронна адреса'}</h2>
              <ul>
                <li className='contact'>
                  <a href={`mailto:${contactInfo.email}`}>{contactInfo.email}</a>
                </li>
              </ul>
            </section>
          )}

          {contactInfo?.addressText && (
            <section className='contacts-block'>
              <h2 className='contacts-subtitle'>{language === 'en' ? 'Address' : 'Адреса'}</h2>
              <ul>
                <li className='contact'>
                  <a href={contactInfo.addressUrl || undefined} target='_blank' rel='noreferrer'>
                    {contactInfo.addressText}
                  </a>
                </li>
              </ul>
            </section>
          )}

          {contactInfo && (contactInfo.bankRecipient || contactInfo.bankIban) && (
            <section className='contacts-block contacts-block--wide'>
              <h2 className='contacts-subtitle'>{language === 'en' ? 'Bank details' : 'Банківські реквізити'}</h2>
              <dl className='bank-list'>
                {contactInfo.bankRecipient && (
                  <div>
                    <dt>{language === 'en' ? 'Recipient' : 'Найменування отримувача'}</dt>
                    <dd>{contactInfo.bankRecipient}</dd>
                  </div>
                )}
                {contactInfo.bankRecipientCode && (
                  <div>
                    <dt>{language === 'en' ? 'Recipient code' : 'Код одержувача'}</dt>
                    <dd className='tnum'>{contactInfo.bankRecipientCode}</dd>
                  </div>
                )}
                {contactInfo.bankIban && (
                  <div>
                    <dt>IBAN</dt>
                    <dd className='tnum'>{contactInfo.bankIban}</dd>
                  </div>
                )}
                {contactInfo.bankName && (
                  <div>
                    <dt>{language === 'en' ? 'Bank' : 'Назва банку'}</dt>
                    <dd>{contactInfo.bankName}</dd>
                  </div>
                )}
              </dl>
            </section>
          )}

          {socials.length > 0 && (
            <section className='contacts-block'>
              <h2 className='contacts-subtitle'>{language === 'en' ? 'Social networks' : 'Соціальні мережі'}</h2>
              <ul>
                {socials.map((social) => {
                  const meta = SOCIAL_NETWORKS[social.network];
                  if (!meta) return null;

                  return (
                    <li className='contact' key={social.id ?? social.network}>
                      <a href={social.url} target='_blank' rel='noreferrer'>
                        {meta.label}
                      </a>
                    </li>
                  );
                })}
              </ul>
            </section>
          )}
        </div>
      </div>
    </article>
  );
}
